import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Data, Router} from '@angular/router';
import {NgbOffcanvas} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {debounceTime, Subject, Subscription} from "rxjs";
import {USER_PROFILE_DASHBOARD_ROUTE, LOGIN_ROUTE} from 'src/app/app-routing.module';
import {environment} from "@environment";
import {AbstractComponent, CurrentItemType} from "@components";
import {
  ActionResult, CategoryItem,
  CompetitorResults,
  Country,
  EventItem,
  EventItemType,
  FetchEventByIdRequest,
  Lookups, Race, RaceCommercialItem
} from "@model";
import {
  ActionService,
  AuthenticationService,
  EventRaceService,
  EventService, RaceCommercialService
} from "@services";

@Component({
  selector: 'app-event-details',
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.scss']
})
export class EventDetailsComponent extends AbstractComponent implements OnInit {
  @Input('eventDetails') currentEventItem: EventItem | undefined;
  @Input() results: CompetitorResults[] = [];
  @Input() countries: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];

  private addToFavoritesButtonSubject: Subject<EventItem | undefined> = new Subject<EventItem | undefined>();
  eventCountry: Country | undefined;
  eventCurrency: string | undefined;
  raceCommericalItemsMap: Map<Race, RaceCommercialItem[]> = new Map<Race, RaceCommercialItem[]>();
  isPreviewComponent: boolean = false;

  constructor(
    private eventService: EventService,
    public translocoService: TranslocoService,
    private offcanvasService: NgbOffcanvas,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private eventRaceService: EventRaceService,
    private actionService: ActionService, private raceCommercialService: RaceCommercialService) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToRouteDataChange();
    this.eventService.setCurrentEvent(this.currentEventItem);
    this.subscriptions.push(this.subscribeToAddToFavoritesButton());
    this.setEventCountry();
    this.setEventCurrency();
    if ((this.currentEventItem?.racesList?.length ?? 0) > 0) {
      for (let race of this.currentEventItem!.racesList!) {
        this.raceCommercialService.findPublicRaceCommercialItems(race.id!).subscribe((value: RaceCommercialItem[]) => {
          this.raceCommericalItemsMap.set(race, value);
        });
      }
    }
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({isPreview}: Data) => {
        this.isPreviewComponent = isPreview ?? false;
      })
    )
  }

  setEventCountry() {
    if (this.countries !== null && this.countries.length > 0 && this.currentEventItem && this.currentEventItem.country) {
      let country: Lookups | undefined = this.countries.find((item: Lookups) => item.id === this.currentEventItem!.country);
      if (country) {
        this.eventCountry = country;
      }
    }
  }

  setEventCurrency() {
    const curr: Lookups | undefined = this.currencies.find((currency) => currency.id === this.currentEventItem?.currency);
    this.eventCurrency = curr?.name ?? "";
  }

  subscribeToAddToFavoritesButton(): Subscription {
    return this.addToFavoritesButtonSubject.pipe(debounceTime(environment.debounceActionInterval)).subscribe((eventItem: EventItem | undefined) => {
        if (this.authenticationService.isAuthenticated()) {
          this.addToFavoritesAction(eventItem);
        } else {
          this.actionService.addAction(() => this.addToFavoritesAction(eventItem));
          this.actionService.addAction(() => this.router.navigate([USER_PROFILE_DASHBOARD_ROUTE], {queryParams: {typeOfRace: EventItemType.MY_FAVORITE}}));
          this.goToLogin()
        }
      }
    )
  }

  addToFavoritesAction(eventItem: EventItem | undefined): void {
    if (eventItem?.id) {
      this.eventService.findEventById(new FetchEventByIdRequest(eventItem?.id), true).subscribe((value: EventItem | null) => {
        if (value) {
          if (value.id && value.favorite) {
            this.eventRaceService.removeEventFromFavorites(value.id).subscribe((actionResult: ActionResult<boolean>) => {
              if (actionResult.result) {
                this.eventRaceService.fetchUserFavoritesEventRaces();
                eventItem.favorite = false;
              }
            });
          } else if (value?.id && !value.favorite) {
            this.eventRaceService.addEventToFavorites(value.id).subscribe((actionResult: ActionResult<boolean>) => {
              if (actionResult.result) {
                this.eventRaceService.fetchUserFavoritesEventRaces();
                eventItem.favorite = true;
              }
            });
          }
        }
      });
    }
  }

  goToLogin(): void {
    this.offcanvasService.dismiss();
    this.router.navigate([LOGIN_ROUTE], {queryParams: {typeOfRace: EventItemType.MY_FAVORITE}});
  }

  protected readonly CurrentItemType = CurrentItemType;
}
